/**
 * The conversation on a feedback ticket, plus the box to answer it.
 *
 * Shared by the feedback widget, the standalone ticket page a notification mail links to,
 * and the admin dialog — the three places a ticket is read. `viewerRole` decides which side
 * is "mine", so the same component renders correctly whether the reader is the person who
 * reported the issue or the person answering it.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import type { FeedbackMessage, FeedbackAuthorRole } from 'API/feedback';

interface Props {
  messages?: FeedbackMessage[];
  /** The original report, shown as the first turn so the thread reads from the beginning. */
  description?: string;
  /** Who is reading. Their own messages sit on the right. */
  viewerRole: FeedbackAuthorRole;
  onReply?: (body: string) => Promise<void>;
  /** Shown instead of the composer when replying is not possible. */
  replyDisabledHint?: string;
  placeholder?: string;
  /** Scroll the newest message into view on mount — for the page opened straight from a mail. */
  autoScroll?: boolean;
}

const MAX_BODY = 5000;

const authorLabel = (role: FeedbackAuthorRole, viewerRole: FeedbackAuthorRole, authorName?: string) => {
  if (role === viewerRole) return 'Du';
  if (role === 'team') return 'SAIA Team';
  return authorName || 'Melder';
};

const FeedbackThread: React.FC<Props> = ({
  messages,
  description,
  viewerRole,
  onReply,
  replyDisabledHint,
  placeholder = 'Antwort schreiben…',
  autoScroll = false,
}) => {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const list = messages || [];

  useEffect(() => {
    if (autoScroll && list.length > 0) {
      endRef.current?.scrollIntoView({ block: 'nearest' });
    }
    // Only on first render: re-scrolling on every reply would yank the page while typing.
  }, []);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || !onReply) return;
    setSending(true);
    setError(null);
    try {
      await onReply(body);
      setDraft('');
    } catch {
      // The draft is deliberately kept so a failed send does not lose what was written.
      setError('Antwort konnte nicht gesendet werden. Bitte erneut versuchen.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter sends, Shift+Enter breaks the line — as in every chat these replies resemble.
    if (e.key === 'Enter' && !e.shiftKey && !sending) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <Stack gap={1.25}>
      {description && (
        <Box
          sx={{
            alignSelf: viewerRole === 'reporter' ? 'flex-end' : 'flex-start',
            maxWidth: '92%',
            px: 1.5,
            py: 1,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            Ursprüngliche Meldung
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {description}
          </Typography>
        </Box>
      )}

      {list.map((m) => {
        const isMine = m.role === viewerRole;
        return (
          <Box
            key={m._id}
            sx={{
              alignSelf: isMine ? 'flex-end' : 'flex-start',
              maxWidth: '92%',
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: isMine ? 'primary.main' : 'action.hover',
              color: isMine ? 'primary.contrastText' : 'text.primary',
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mb: 0.25 }}>
              {authorLabel(m.role, viewerRole, m.authorName)} · {new Date(m.createdAt).toLocaleString('de-DE')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {m.body}
            </Typography>
          </Box>
        );
      })}

      <div ref={endRef} />

      {onReply ? (
        <Stack direction="row" gap={1} alignItems="flex-end" sx={{ mt: 0.5 }}>
          <TextField
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            multiline
            minRows={2}
            maxRows={8}
            size="small"
            fullWidth
            disabled={sending}
            inputProps={{ maxLength: MAX_BODY }}
            helperText={error || (draft.length > MAX_BODY - 500 ? `${MAX_BODY - draft.length} Zeichen übrig` : ' ')}
            error={!!error}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            sx={{ mb: 3, minWidth: 44, px: 1.5 }}
            aria-label="Antwort senden"
          >
            {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
          </Button>
        </Stack>
      ) : (
        replyDisabledHint && (
          <Typography variant="caption" color="text.secondary">
            {replyDisabledHint}
          </Typography>
        )
      )}
    </Stack>
  );
};

export default FeedbackThread;
